/**
 * The strategic event ring (spec §11.4): one preallocated SoA ring written by the sim and read by any number
 * of independent cursors (the Wire, rollups, views, the local delta), the same pattern as M1's combat ring
 * (`src/combat/events.ts`). Allocation-free (D-014).
 *
 * A ring may mirror selected event types into a second ring. The state's `events` ring mirrors every type
 * except AI `tradeDone` into `notable`, which therefore keeps several faction days of the events cause
 * attribution needs, while `events` wraps within a day at full traffic. The player's trades (agent −1 with faction
 * `FACTION_PLAYER`) are always mirrored; a refit works site's market draw (agent −1 with an AI faction, SNK-8c) is
 * an AI trade and is not (SNK-8e).
 *
 * Reader pattern:
 * ```ts
 * let cursor = 0
 * // each tick:
 * for (let i = firstReadable(ring, cursor); i < ring.head; i++) {
 *   const s = eventSlot(ring, i)
 *   if (ring.type[s] === EV.agentDestroyed) { ... ring.agent[s] ... }
 * }
 * cursor = ring.head
 * ```
 * Field conventions per type are documented on `EV` in `types.ts`.
 */
import { EV, FACTION_PLAYER, type EventType, type StrategicEventRing } from './types'

/**
 * M5 hidden event types (M5 spec §7.2, §9.2; plan §3): evidence hooks for M7 that stay in the rings and the
 * save but never reach the Wire, views or the UI. `HIDDEN_EVENT[type] === 1` marks them; readers that build
 * player-facing output skip them (`isHiddenEvent`).
 */
export const HIDDEN_EVENT_TYPES: readonly EventType[] = [EV.varroStoke, EV.holdfastFundsGuardians]
export const HIDDEN_EVENT: Readonly<Uint8Array> = (() => {
  const t = new Uint8Array(256)
  for (const e of HIDDEN_EVENT_TYPES) t[e] = 1
  return t
})()

/** True for event types that must never reach the Wire, views or the UI. Allocation-free. */
export function isHiddenEvent(type: number): boolean {
  return HIDDEN_EVENT[type] === 1
}

/**
 * M6 event types (M6 spec §5-§8; plan P0): everything from `shipBought` on. The fleet feed and captain logs (P10,
 * P13) read them from the notable ring; `FLEET_EVENT[type] === 1` marks them (allocation-free `isFleetEvent`).
 */
export const M6_EVENT_TYPES: readonly EventType[] = (Object.values(EV) as EventType[]).filter((t) => t >= EV.shipBought && t <= EV.insurancePaid)
export const FLEET_EVENT: Readonly<Uint8Array> = (() => {
  const t = new Uint8Array(256)
  for (const e of M6_EVENT_TYPES) t[e] = 1
  return t
})()

/** True for the M6 fleet and corporation event types. Allocation-free. */
export function isFleetEvent(type: number): boolean {
  return FLEET_EVENT[type] === 1
}

/** M7 event types (plan §2, S16): everything from `storyTierDrop` on. None is hidden; S17–S24 emit them. */
export const M7_EVENT_TYPES: readonly EventType[] = (Object.values(EV) as EventType[]).filter((t) => t >= EV.storyTierDrop)

/** Every event type except `tradeDone`: what the notable ring keeps. */
export const NOTABLE_TYPES: readonly EventType[] = (Object.values(EV) as EventType[]).filter((t) => t !== EV.tradeDone)

export function createEventRing(
  capacity: number,
  mirror: StrategicEventRing | null = null,
  mirrorTypes: readonly number[] = [],
): StrategicEventRing {
  if (!(capacity > 0) || (capacity & (capacity - 1)) !== 0) throw new Error(`createEventRing: capacity ${capacity} must be a power of two`)
  const mirrorMask = new Uint8Array(256)
  for (const t of mirrorTypes) mirrorMask[t] = 1
  return {
    capacity,
    mask: capacity - 1,
    type: new Uint8Array(capacity),
    t: new Float64Array(capacity),
    sys: new Uint16Array(capacity),
    station: new Int32Array(capacity),
    agent: new Int32Array(capacity),
    faction: new Uint8Array(capacity),
    other: new Uint8Array(capacity),
    ware: new Int8Array(capacity),
    qty: new Float32Array(capacity),
    value: new Float32Array(capacity),
    cause: new Uint8Array(capacity),
    aux: new Int32Array(capacity),
    head: 0,
    mirror,
    mirrorMask,
  }
}

function write(
  ring: StrategicEventRing,
  type: EventType,
  t: number,
  sys: number,
  station: number,
  agent: number,
  faction: number,
  other: number,
  ware: number,
  qty: number,
  value: number,
  cause: number,
  aux: number,
): number {
  const seq = ring.head
  const s = seq & ring.mask
  ring.type[s] = type
  ring.t[s] = t
  ring.sys[s] = sys
  ring.station[s] = station
  ring.agent[s] = agent
  ring.faction[s] = faction
  ring.other[s] = other
  ring.ware[s] = ware
  ring.qty[s] = qty
  ring.value[s] = value
  ring.cause[s] = cause
  ring.aux[s] = aux
  ring.head = seq + 1
  return seq
}

/**
 * Appends an event, overwriting the oldest when full, and copies it to the mirror ring when its type is
 * mirrored or it is the player's (`agent < 0` and `faction` `FACTION_PLAYER`; SNK-8e: agent −1 alone is also a
 * refit works site's draw, an AI trade). Returns the event's sequence number in `ring`.
 */
export function emit(
  ring: StrategicEventRing,
  type: EventType,
  t: number,
  sys: number,
  station: number,
  agent: number,
  faction: number,
  other: number,
  ware: number,
  qty: number,
  value: number,
  cause: number,
  aux: number,
): number {
  const m = ring.mirror
  if (m !== null && (ring.mirrorMask[type] === 1 || (agent < 0 && faction === FACTION_PLAYER))) write(m, type, t, sys, station, agent, faction, other, ware, qty, value, cause, aux)
  return write(ring, type, t, sys, station, agent, faction, other, ware, qty, value, cause, aux)
}

/** The first sequence number a reader at `cursor` can still read: a reader that fell more than a ring behind skips to the oldest event. */
export function firstReadable(ring: StrategicEventRing, cursor: number): number {
  const oldest = ring.head - ring.capacity
  return cursor > oldest ? cursor : oldest > 0 ? oldest : 0
}

/** Ring slot of sequence number `i`. */
export function eventSlot(ring: StrategicEventRing, i: number): number {
  return i & ring.mask
}
